import { addNote, editNote, deleteNote, showNote, removeNoteContainer, createNoteIndicator, updateNoteContainer, completeAction, setActionData, showHideNote, navigateNextPrevNote, showAllNotes, processSheetNotes, noteUndoRedo } from '../common/index';
import { isNullOrUndefined, EventHandler, closest, detach, getUniqueID } from '@syncfusion/ej2-base';
import { getCellIndexes, getSortedIndex, getRangeAddress, sheetsDestroyed } from '../../workbook/common/index';
import { getCell, updateCell, getSheetName, getRowHeight } from '../../workbook/index';
import { setCell, importModelUpdate } from '../../workbook/index';
/**
 * `Note` module
 */
export class SpreadsheetNote {
    /**
     * Constructor for Note module.
     *
     * @param {Spreadsheet} parent - Constructor for Note module.
     */
    constructor(parent) {
        /** @hidden */
        this.activeNoteCell = null;
        /** @hidden */
        this.isShowAllNotes = false;
        this.parent = parent;
        this.addEventListener();
    }
    /**
     * To destroy the Note module.
     *
     * @returns {void} - To destroy the Note module.
     */
    destroy() {
        this.removeEventListener();
        if (!this.parent.isDestroyed && !this.parent.refreshing) {
            const noteIndicators = this.parent.element.getElementsByClassName('e-addNoteIndicator');
            while (noteIndicators.length) {
                const cellEle = closest(noteIndicators[0], '.e-cell');
                if (cellEle) {
                    EventHandler.remove(cellEle, 'mouseover', this.mouseOver);
                    EventHandler.remove(cellEle, 'mouseout', this.mouseOut);
                }
                detach(noteIndicators[0]);
            }
            this.parent.sheets.forEach((sheet) => {
                if (sheet.notes) {
                    sheet.notes.forEach((note) => this.removeNoteElement(note));
                }
            });
        }
        this.activeNoteCell = null;
        this.isShowAllNotes = null;
        this.parent = null;
    }
    addEventListener() {
        this.parent.on(addNote, this.addNote, this);
        this.parent.on(editNote, this.editNote, this);
        this.parent.on(deleteNote, this.deleteNote, this);
        this.parent.on(createNoteIndicator, this.createNoteIndicator, this);
        this.parent.on(showNote, this.showNote, this);
        this.parent.on(removeNoteContainer, this.removeNoteContainer, this);
        this.parent.on(updateNoteContainer, this.updateNoteContainer, this);
        this.parent.on(showHideNote, this.showHideNote, this);
        this.parent.on(importModelUpdate, this.updateNotesFromSheet, this);
        this.parent.on(navigateNextPrevNote, this.navigateNextPrevNote, this);
        this.parent.on(showAllNotes, this.showAllNotes, this);
        this.parent.on(processSheetNotes, this.processSheetNotes, this);
        this.parent.on(noteUndoRedo, this.noteUndoRedo, this);
        this.parent.on(sheetsDestroyed, this.sheetDestroyHandler, this);
    }
    removeEventListener() {
        if (!this.parent.isDestroyed) {
            this.parent.off(addNote, this.addNote);
            this.parent.off(editNote, this.editNote);
            this.parent.off(showNote, this.showNote);
            this.parent.off(deleteNote, this.deleteNote);
            this.parent.off(createNoteIndicator, this.createNoteIndicator);
            this.parent.off(removeNoteContainer, this.removeNoteContainer);
            this.parent.off(updateNoteContainer, this.updateNoteContainer);
            this.parent.off(showHideNote, this.showHideNote);
            this.parent.off(importModelUpdate, this.updateNotesFromSheet);
            this.parent.off(navigateNextPrevNote, this.navigateNextPrevNote);
            this.parent.off(showAllNotes, this.showAllNotes);
            this.parent.off(processSheetNotes, this.processSheetNotes);
            this.parent.off(noteUndoRedo, this.noteUndoRedo);
            this.parent.off(sheetsDestroyed, this.sheetDestroyHandler);
        }
    }
    /**
     * Gets the module name.
     *
     * @returns {string} - Gets the module name.
     */
    getModuleName() {
        return 'spreadsheetNote';
    }
    getNoteId(note) {
        return `e-note-container-${note.id}`;
    }
    getNoteConnectorId(note) {
        return `e-note-connector-${note.id}`;
    }
    getNoteByCellIndex(rowIndex, columnIndex) {
        const sheet = this.parent.getActiveSheet();
        if (sheet && sheet.notes) {
            return sheet.notes.find((note) => note.rowIdx === rowIndex && note.colIdx === columnIndex);
        }
        return undefined;
    }
    showHideNote() {
        const sheet = this.parent.getActiveSheet();
        const idx = getCellIndexes(sheet.activeCell);
        const note = this.getNoteByCellIndex(idx[0], idx[1]);
        if (note) {
            note.isVisible = !note.isVisible;
            updateCell(this.parent, sheet, { rowIdx: note.rowIdx, colIdx: note.colIdx, preventEvt: true,
                cell: { notes: note } });
            if (note.isVisible) {
                this.parent.notify(showNote, { rowIndex: idx[0], columnIndex: idx[1], isNoteEditable: false, isScrollWithNote: true });
            }
            else {
                this.removeNoteElement(note);
            }
        }
    }
    addNote() {
        const sheet = this.parent.getActiveSheet();
        const cellIndexes = getCellIndexes(this.parent.getActiveSheet().activeCell);
        const targetElement = this.parent.getCell(cellIndexes[0], cellIndexes[1]);
        if (!isNullOrUndefined(targetElement) && ((targetElement.children.length === 0) || (targetElement.children.length > 0 && targetElement.children[targetElement.childElementCount - 1].className.indexOf('e-addNoteIndicator') === -1))) {
            const note = { id: getUniqueID('e_note'), rowIdx: cellIndexes[0], colIdx: cellIndexes[1], text: '', isVisible: this.isShowAllNotes };
            if (!sheet.notes) {
                sheet.notes = [];
            }
            this.activeNoteCell = [cellIndexes[0], cellIndexes[1]];
            updateCell(this.parent, sheet, { rowIdx: cellIndexes[0], colIdx: cellIndexes[1], preventEvt: true, cell: { isNoteEditable: true } });
            this.parent.setUsedRange(cellIndexes[0], cellIndexes[1], sheet);
            this.insertNoteSorted(sheet, note);
            this.createNoteIndicator({ targetElement: targetElement, rowIndex: cellIndexes[0], columnIndex: cellIndexes[1] });
            this.createNoteContainer(note, targetElement, false, true, false, `${sheet.name}!${sheet.activeCell}`);
        }
    }
    deleteNote(args) {
        const sheet = this.parent.getActiveSheet();
        const cellIndexes = getCellIndexes(this.parent.getActiveSheet().activeCell);
        const rowIndex = !isNullOrUndefined(args) && !isNullOrUndefined(args.rowIndex) ? args.rowIndex : cellIndexes[0];
        const columnIndex = !isNullOrUndefined(args) && !isNullOrUndefined(args.columnIndex) ? args.columnIndex : cellIndexes[1];
        const targetElement = this.parent.getCell(rowIndex, columnIndex);
        if (targetElement.children.length > 0 && targetElement.children[targetElement.children.length - 1].className.indexOf('e-addNoteIndicator') > -1) {
            targetElement.removeChild(targetElement.children[targetElement.children.length - 1]);
            EventHandler.remove(targetElement, 'mouseover', this.mouseOver);
            EventHandler.remove(targetElement, 'mouseout', this.mouseOut);
            const address = getSheetName(this.parent, this.parent.activeSheetIndex) + '!' + this.parent.getActiveSheet().activeCell;
            const note = this.getNoteByCellIndex(rowIndex, columnIndex);
            if (!isNullOrUndefined(args) && args.isDeleteFromMenu) {
                this.parent.notify(setActionData, { args: { action: 'beforeCellSave', eventArgs: { address: address } } });
            }
            if (note) {
                this.removeNoteElement(note);
            }
            this.syncNoteToSheetArray(sheet, rowIndex, columnIndex, null);
            const cell = getCell(rowIndex, columnIndex, sheet);
            if (cell && cell.notes) {
                delete cell.notes;
                updateCell(this.parent, sheet, { rowIdx: rowIndex, colIdx: columnIndex, preventEvt: true, cell: cell });
            }
            if (this.activeNoteCell && this.activeNoteCell[0] === rowIndex && this.activeNoteCell[1] === columnIndex) {
                this.activeNoteCell = null;
            }
            if (!isNullOrUndefined(args) && args.isDeleteFromMenu) {
                const eventArgs = { notes: note, address: address };
                this.parent.notify(completeAction, { eventArgs: eventArgs, action: 'deleteNote' });
            }
        }
    }
    editNote() {
        const cellIndexes = !isNullOrUndefined(this.activeNoteCell) ?
            this.activeNoteCell : getCellIndexes(this.parent.getActiveSheet().activeCell);
        const note = this.getNoteByCellIndex(cellIndexes[0], cellIndexes[1]);
        if (note) {
            this.showNote({ rowIndex: cellIndexes[0], columnIndex: cellIndexes[1], isNoteEditable: true });
            const noteContainerElement = document.getElementById(this.getNoteId(note));
            updateCell(this.parent, this.parent.getActiveSheet(), { rowIdx: cellIndexes[0], colIdx: cellIndexes[1], preventEvt: true,
                cell: { isNoteEditable: true } });
            if (noteContainerElement) {
                this.getNoteFocus(noteContainerElement);
            }
        }
    }
    createNoteIndicator(args) {
        const triangleDirection = this.parent.enableRtl
            ? 'left: 0; border-right: 8px solid transparent;' : 'right: 0; border-left: 8px solid transparent;';
        const noteIndicator = this.parent.createElement('div', { className: 'e-addNoteIndicator',
            styles: `position: absolute; top: 0; width: 0; height: 0; border-top: 8px solid red; cursor: pointer; ${triangleDirection}` });
        if (args.targetElement.children.length > 0) {
            const rowHeight = getRowHeight(this.parent.getActiveSheet(), args.rowIndex);
            const defaultFilterButtonHeight = 20;
            for (let i = 0; i < args.targetElement.childElementCount; i++) {
                if (args.targetElement.children[i].className.indexOf('e-filter-btn') > -1) {
                    if (this.parent.enableRtl) {
                        noteIndicator.style.left = (rowHeight < (defaultFilterButtonHeight + 10) ?
                            (args.targetElement.children[i].getBoundingClientRect().width <= 0 ? defaultFilterButtonHeight :
                                args.targetElement.children[i].getBoundingClientRect().width) : 0 + 2) + 'px';
                    }
                    else {
                        noteIndicator.style.right = (rowHeight < (defaultFilterButtonHeight + 10) ?
                            (args.targetElement.children[i].getBoundingClientRect().width <= 0 ? defaultFilterButtonHeight :
                                args.targetElement.children[i].getBoundingClientRect().width) : 0 + 2) + 'px';
                    }
                }
                if (args.targetElement.children[i].className.indexOf('e-validation-list') > -1) {
                    if (this.parent.enableRtl) {
                        noteIndicator.style.left = `${(args.targetElement.children[i].getBoundingClientRect().width || 20) + 2}px`;
                    }
                    else {
                        noteIndicator.style.right = `${(args.targetElement.children[i].getBoundingClientRect().width || 20) + 2}px`;
                    }
                }
            }
        }
        if (!isNullOrUndefined(args.targetElement) && (args.targetElement.children.length === 0) || (args.targetElement.children.length > 0 && args.targetElement.children[args.targetElement.childElementCount - 1].className.indexOf('e-addNoteIndicator') === -1)) {
            if (args.cell && typeof args.cell.notes === 'string') {
                const note = { id: getUniqueID('e_note'), rowIdx: args.rowIndex, colIdx: args.columnIndex,
                    text: args.cell.notes, isVisible: this.isShowAllNotes };
                args.cell.notes = note;
                const sheet = this.parent.getActiveSheet();
                updateCell(this.parent, sheet, { rowIdx: args.rowIndex, colIdx: args.columnIndex, preventEvt: true, cell: { notes: note } });
                this.syncNoteToSheetArray(sheet, note.rowIdx, note.colIdx, note);
            }
            if (!args.skipEvent) {
                EventHandler.add(args.targetElement, 'mouseover', this.mouseOver, [this, args.rowIndex, args.columnIndex]);
                EventHandler.add(args.targetElement, 'mouseout', this.mouseOut, this);
            }
            args.targetElement.appendChild(noteIndicator);
        }
    }
    mouseOver() {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const args = this;
        const noteModule = args[0];
        const rowIndex = args[1];
        const columnIndex = args[2];
        const note = noteModule.getNoteByCellIndex(rowIndex, columnIndex);
        if (!note || note.isVisible) {
            return;
        }
        const sheet = noteModule.parent.getActiveSheet();
        if (sheet.notes) {
            let isOtherNoteEditState;
            sheet.notes.forEach((note) => {
                if ((note.rowIdx !== rowIndex || note.colIdx !== columnIndex)) {
                    const cell = note.rowIdx !== undefined && getCell(note.rowIdx, note.colIdx, sheet, false, true);
                    if (cell && cell.isNoteEditable) {
                        isOtherNoteEditState = true;
                    }
                    else if (!note.isVisible) {
                        noteModule.removeNoteElement(note);
                    }
                }
            });
            if (isOtherNoteEditState) {
                return;
            }
        }
        noteModule.activeNoteCell = [rowIndex, columnIndex];
        if (!note || !noteModule.isNoteElementVisible(note)) {
            noteModule.showNote({ rowIndex: rowIndex, columnIndex: columnIndex, isNoteEditable: false });
        }
    }
    mouseOut(e) {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const args = this;
        const noteModule = args[0] ? args[0] : this.parent.spreadsheetNoteModule;
        const rowIndex = !isNullOrUndefined(args[1]) ? args[1] : (this.activeNoteCell ? this.activeNoteCell[0] : undefined);
        const columnIndex = !isNullOrUndefined(args[2]) ? args[2] : (this.activeNoteCell ? this.activeNoteCell[1] : undefined);
        const note = noteModule.getNoteByCellIndex(rowIndex, columnIndex);
        if (!note || note.isVisible) {
            return;
        }
        const relatedTarget = e.relatedTarget;
        const noteId = noteModule.getNoteId(note);
        const noteContainer = noteId && document.getElementById(noteId);
        const noteConnectorId = noteModule.getNoteConnectorId(note);
        const noteConnector = noteConnectorId && document.getElementById(noteConnectorId);
        if (noteContainer) {
            if (!(document.activeElement.className.indexOf('e-addNoteContainer') > -1 ||
                (relatedTarget && ((relatedTarget === noteContainer) || (relatedTarget === noteConnector))))) {
                noteModule.removeNoteElement(note);
                this.activeNoteCell = null;
            }
        }
    }
    isNoteElementVisible(note) {
        const noteContainer = document.getElementById(this.getNoteId(note));
        return noteContainer && noteContainer.style.display !== 'none';
    }
    containerFocusIn(e) {
        e.target.style.zIndex = '6';
    }
    containerFocusOut(e) {
        e.target.style.zIndex = '5';
    }
    createNoteContainer(note, targetElement, isShowNote, isNoteEditable, isRender, address) {
        const cellRect = targetElement.getBoundingClientRect();
        const noteId = this.getNoteId(note);
        const connectorId = this.getNoteConnectorId(note);
        let noteContainer = document.getElementById(noteId);
        let connectorLine = document.getElementById(connectorId);
        if (noteContainer && connectorLine) {
            this.updateExistingNoteDisplay(noteContainer, connectorLine, note, cellRect, isShowNote, isNoteEditable, isRender);
        }
        else {
            noteContainer = this.parent.createElement('textarea', { id: noteId, className: 'e-addNoteContainer' });
            connectorLine = this.parent.createElement('canvas', { id: connectorId, className: 'e-connectorLine' });
            this.createContainer(noteContainer, note, cellRect, isShowNote, isRender);
            this.createConnectorLine(connectorLine, noteContainer, cellRect);
            if (address) {
                const eventArgs = { notes: note, noteContainer: noteContainer, address: address };
                this.parent.notify(completeAction, { eventArgs: eventArgs, action: 'addNoteOpen' });
            }
            if (isNoteEditable) {
                this.getNoteFocus(noteContainer);
            }
            EventHandler.add(noteContainer, 'focus', this.containerFocusIn, this);
            EventHandler.add(noteContainer, 'blur', this.containerFocusOut, this);
            EventHandler.add(noteContainer, 'mouseout', this.mouseOut, [this, note.rowIdx, note.colIdx]);
            EventHandler.add(connectorLine, 'mouseout', this.mouseOut, [this, note.rowIdx, note.colIdx]);
        }
        EventHandler.remove(targetElement, 'mouseout', this.mouseOut);
        EventHandler.add(targetElement, 'mouseout', this.mouseOut, [this, note.rowIdx, note.colIdx]);
    }
    updateExistingNoteDisplay(noteContainer, connectorLine, note, cellRect, isShowNote, isNoteEditable, isRender) {
        this.createContainer(noteContainer, note, cellRect, isShowNote, isRender);
        this.createConnectorLine(connectorLine, noteContainer, cellRect);
        if (isShowNote && (!noteContainer.value || noteContainer.value !== note.text)) {
            noteContainer.value = note.text || '';
        }
        if (isNoteEditable) {
            this.getNoteFocus(noteContainer);
        }
    }
    getNoteFocus(noteContainerElement) {
        noteContainerElement.selectionStart = noteContainerElement.value.length;
        noteContainerElement.focus();
    }
    createContainer(noteContainer, note, cellRect, isShowNote, isRender) {
        let containerTop = 5;
        const selectAllCell = this.parent.element.getElementsByClassName('e-select-all-cell')[0];
        const scroller = this.parent.element.getElementsByClassName('e-scroller')[0];
        if (!isNullOrUndefined(selectAllCell) && !isNullOrUndefined(scroller) &&
            cellRect.top >= selectAllCell.getBoundingClientRect().bottom &&
            cellRect.top <= scroller.getBoundingClientRect().top) {
            const isViewableArea = this.parent.enableRtl ?
                cellRect.left <= selectAllCell.getBoundingClientRect().left &&
                    cellRect.left >= scroller.getBoundingClientRect().left :
                cellRect.right >= selectAllCell.getBoundingClientRect().right &&
                    cellRect.right <= scroller.getBoundingClientRect().right;
            if (isViewableArea) {
                noteContainer.style.display = 'block';
                containerTop = cellRect.top === selectAllCell.getBoundingClientRect().bottom ? 0 : containerTop;
            }
            else {
                noteContainer.style.display = 'none';
            }
        }
        else {
            noteContainer.style.display = 'none';
        }
        const elementClientRect = this.parent.element.getBoundingClientRect();
        const elementPosition = this.parent.element.style.getPropertyValue('position');
        noteContainer.style.position = 'absolute';
        noteContainer.style.top = (cellRect.top - (elementClientRect.top - (elementPosition === 'absolute' ? 0 :
            this.parent.element.offsetTop)) - containerTop) + 'px';
        let leftPos;
        const noteWidth = 120;
        const offsetLeft = (elementPosition === 'absolute' ? 0 : this.parent.element.offsetLeft);
        if (this.parent.enableRtl) {
            leftPos = cellRect.left - (elementClientRect.left - offsetLeft) - noteWidth - 10;
        }
        else {
            leftPos = cellRect.left + cellRect.width - (elementClientRect.left - offsetLeft) + 10;
        }
        noteContainer.style.left = `${leftPos}px`;
        noteContainer.style.width = noteContainer.style.width || `${noteWidth}px`;
        noteContainer.style.height = noteContainer.style.height || '120px';
        noteContainer.style.zIndex = '5';
        noteContainer.style.color = 'black';
        noteContainer.style.backgroundColor = 'lightyellow';
        if (isShowNote && !isNullOrUndefined(note) && !isNullOrUndefined(note.text)) {
            noteContainer.innerHTML = note.text;
        }
        else {
            noteContainer.innerHTML = this.parent.author ? this.parent.author + ':\n' : '';
        }
        const isActiveNote = isRender && document.activeElement === noteContainer;
        this.parent.element.appendChild(noteContainer);
        if (isActiveNote) {
            this.getNoteFocus(noteContainer);
        }
    }
    createConnectorLine(connectorLine, noteContainer, cellRect) {
        const lineWidth = 100;
        connectorLine.style.width = `${lineWidth}px`;
        connectorLine.style.position = 'absolute';
        connectorLine.style.zIndex = '1';
        const context = connectorLine.getContext('2d');
        const elementClientRect = this.parent.element.getBoundingClientRect();
        const elementPosition = this.parent.element.style.getPropertyValue('position');
        const offsetLeft = (elementPosition === 'absolute' ? 0 : this.parent.element.offsetLeft);
        if (this.parent.enableRtl) {
            connectorLine.style.left = cellRect.left - (elementClientRect.left - offsetLeft) - lineWidth + 'px';
            connectorLine.style.transform = 'scaleX(-1)';
        }
        else {
            connectorLine.style.left = cellRect.left + cellRect.width - (elementClientRect.left - offsetLeft) + 'px';
        }
        connectorLine.style.top = (noteContainer.getBoundingClientRect().top - (elementClientRect.top - (elementPosition === 'absolute' ?
            0 : this.parent.element.offsetTop)) - 5) + 'px';
        context.clearRect(0, 0, connectorLine.width, connectorLine.height);
        context.beginPath();
        if (noteContainer.getBoundingClientRect().top === cellRect.top) {
            context.moveTo(0, 16);
            context.lineTo(30, 15);
        }
        else {
            context.moveTo(0, 30);
            context.lineTo(30, 15);
        }
        context.strokeStyle = 'black';
        context.lineWidth = 5;
        context.stroke();
        this.parent.element.appendChild(connectorLine);
        if (noteContainer.getBoundingClientRect().top > 0) {
            connectorLine.style.display = 'block';
            connectorLine.style.zIndex = '4';
        }
        else {
            connectorLine.style.display = 'none';
        }
    }
    showNote(args) {
        const note = this.getNoteByCellIndex(args.rowIndex, args.columnIndex);
        if (note) {
            const targetElement = !isNullOrUndefined(this.parent.getCell(args.rowIndex, args.columnIndex)) ?
                this.parent.getCell(args.rowIndex, args.columnIndex) : args.cellElement;
            if (!isNullOrUndefined(targetElement)
                && (args.isScrollWithNote || (targetElement.children !== null && targetElement.children.length > 0 &&
                    targetElement.children[targetElement.children.length - 1].classList.contains('e-addNoteIndicator')))) {
                this.activeNoteCell = [args.rowIndex, args.columnIndex];
                this.createNoteContainer(note, targetElement, true, args.isNoteEditable, args.isRender);
            }
        }
    }
    removeNoteContainer(args) {
        const sheet = this.parent.getActiveSheet();
        if (args && !isNullOrUndefined(args.rowIndex) && !isNullOrUndefined(args.columnIndex)) {
            const cell = getCell(args.rowIndex, args.columnIndex, sheet, false, true);
            const note = cell.notes;
            if (note) {
                this.removeNoteElement(note);
            }
        }
        else if (isNullOrUndefined(args)) {
            if (sheet.notes) {
                sheet.notes.forEach((note) => {
                    if (!note.isVisible) {
                        this.removeNoteElement(note);
                    }
                });
            }
        }
    }
    removeNoteElement(note) {
        const noteContainer = document.getElementById(this.getNoteId(note));
        const connectorLine = document.getElementById(this.getNoteConnectorId(note));
        if (noteContainer) {
            EventHandler.remove(noteContainer, 'mouseout', this.mouseOut);
            EventHandler.remove(noteContainer, 'focus', this.containerFocusIn);
            EventHandler.remove(noteContainer, 'blur', this.containerFocusOut);
            if (this.parent.element.contains(noteContainer)) {
                this.parent.element.removeChild(noteContainer);
            }
        }
        if (connectorLine) {
            EventHandler.remove(connectorLine, 'mouseout', this.mouseOut);
            if (this.parent.element.contains(connectorLine)) {
                this.parent.element.removeChild(connectorLine);
            }
        }
        const targetElement = this.parent.getCell(note.rowIdx, note.colIdx);
        if (targetElement) {
            EventHandler.remove(targetElement, 'mouseout', this.mouseOut);
        }
        if (this.activeNoteCell && this.activeNoteCell[0] === note.rowIdx && this.activeNoteCell[1] === note.colIdx) {
            this.activeNoteCell = null;
        }
        const cell = note.rowIdx !== undefined && getCell(note.rowIdx, note.colIdx, this.parent.getActiveSheet(), false, true);
        if (cell && cell.notes && cell.isNoteEditable) {
            cell.isNoteEditable = false;
        }
    }
    updateNoteContainer() {
        const sheet = this.parent.getActiveSheet();
        this.removeNoteElementsFromOtherSheets();
        if (!sheet.notes || sheet.notes.length === 0) {
            if (this.parent.selectionModule) {
                this.parent.selectionModule.isNoteActiveElement = false;
            }
            if (this.activeNoteCell) {
                this.activeNoteCell = null;
            }
            return;
        }
        this.parent.selectionModule.isNoteActiveElement = false;
        let activeNoteFound = false;
        sheet.notes.forEach((note) => {
            const noteContainer = document.getElementById(this.getNoteId(note));
            const hasFocus = noteContainer ? (document.activeElement === noteContainer ||
                (note.rowIdx !== undefined && getCell(note.rowIdx, note.colIdx, sheet, false, true).isNoteEditable)) : false;
            if (hasFocus) {
                activeNoteFound = true;
                this.parent.selectionModule.isNoteActiveElement = true;
                this.activeNoteCell = [note.rowIdx, note.colIdx];
                if ((isNullOrUndefined(note.text) && noteContainer.value) || note.text !== noteContainer.value) {
                    const address = getSheetName(this.parent, this.parent.activeSheetIndex) + '!' + getRangeAddress([note.rowIdx, note.colIdx]);
                    this.parent.notify(setActionData, { args: { action: 'beforeCellSave', eventArgs: { address: address } } });
                    const eventAction = note.text ? 'editNote' : 'addNote';
                    note.text = noteContainer.value;
                    updateCell(this.parent, sheet, { rowIdx: note.rowIdx, colIdx: note.colIdx, preventEvt: true,
                        cell: { notes: note, isNoteEditable: true } });
                    this.syncNoteToSheetArray(sheet, note.rowIdx, note.colIdx, note);
                    const eventArgs = { notes: note, address: address };
                    this.parent.notify(completeAction, { eventArgs: eventArgs, action: eventAction });
                }
            }
            if (note.isVisible || hasFocus) {
                this.parent.notify(showNote, { rowIndex: note.rowIdx, columnIndex: note.colIdx, isNoteEditable: hasFocus,
                    isScrollWithNote: true });
            }
            else {
                this.removeNoteElement(note);
            }
        });
        if (!activeNoteFound) {
            this.activeNoteCell = null;
        }
    }
    sheetDestroyHandler(args) {
        const noteContainers = [].slice.call(this.parent.element.getElementsByClassName('e-addNoteContainer'));
        noteContainers.forEach((noteContainer) => {
            EventHandler.remove(noteContainer, 'mouseout', this.mouseOut);
            EventHandler.remove(noteContainer, 'focus', this.containerFocusIn);
            EventHandler.remove(noteContainer, 'blur', this.containerFocusOut);
            noteContainer.parentElement.removeChild(noteContainer);
        });
        const connectorLines = [].slice.call(this.parent.element.getElementsByClassName('e-connectorLine'));
        connectorLines.forEach((connectorLine) => {
            EventHandler.remove(connectorLine, 'mouseout', this.mouseOut);
            connectorLine.parentElement.removeChild(connectorLine);
        });
        if (isNullOrUndefined(args.sheetIndex)) {
            this.isShowAllNotes = false;
        }
        this.activeNoteCell = null;
    }
    removeNoteElementsFromOtherSheets() {
        const activeSheetIndex = this.parent.activeSheetIndex;
        this.parent.sheets.forEach((sheet, index) => {
            if (index === activeSheetIndex) {
                return;
            }
            if (sheet.notes) {
                sheet.notes.forEach((note) => {
                    this.removeNoteElement(note);
                });
            }
        });
    }
    insertNoteSorted(sheet, note) {
        if (!sheet.notes) {
            sheet.notes = [];
        }
        const idx = getSortedIndex(sheet.notes, [note.rowIdx, note.colIdx], false);
        sheet.notes.splice(idx, 0, note);
    }
    navigateNextPrevNote(args) {
        const sheetCount = this.parent.sheets.length;
        const startSheetIdx = this.parent.activeSheetIndex;
        const activeSheet = this.parent.getActiveSheet();
        const addr = getCellIndexes(activeSheet.activeCell);
        const currentColl = activeSheet.notes ? activeSheet.notes : [];
        if (currentColl.length > 0) {
            const pos = getSortedIndex(currentColl, addr, false);
            let exactIdx = -1;
            if (pos < currentColl.length) {
                const note = currentColl[pos];
                if (note.rowIdx === addr[0] && note.colIdx === addr[1]) {
                    exactIdx = pos;
                }
            }
            if (exactIdx === -1 && pos > 0) {
                const prevNote = currentColl[pos - 1];
                if (prevNote.rowIdx === addr[0] && prevNote.colIdx === addr[1]) {
                    exactIdx = pos - 1;
                }
            }
            const idx = args.isNext ? (exactIdx > -1 ? exactIdx + 1 : pos) : (exactIdx > -1 ? exactIdx - 1 : pos - 1);
            if (idx >= 0 && idx < currentColl.length) {
                this.navigateToNote(currentColl[idx], startSheetIdx);
                return;
            }
        }
        for (let i = 1; i < sheetCount; i++) {
            const sheetIdx = (startSheetIdx + (args.isNext ? i : -i) + sheetCount) % sheetCount;
            const sheet = this.parent.sheets[sheetIdx];
            if (sheet.notes && sheet.notes.length > 0) {
                const note = args.isNext ? sheet.notes[0] : sheet.notes[sheet.notes.length - 1];
                this.navigateToNote(note, sheetIdx);
                return;
            }
        }
        if (currentColl.length > 0) {
            const note = args.isNext ? currentColl[0] : currentColl[currentColl.length - 1];
            if (note.rowIdx !== addr[0] || note.colIdx !== addr[1]) {
                this.navigateToNote(note, startSheetIdx);
            }
        }
    }
    navigateToNote(note, sheetIndex) {
        const sheetName = getSheetName(this.parent, sheetIndex);
        const address = getRangeAddress([note.rowIdx, note.colIdx]);
        this.parent.goTo(`${sheetName}!${address}`);
        setTimeout(() => {
            this.activeNoteCell = [note.rowIdx, note.colIdx];
            this.parent.notify(editNote, null);
        });
    }
    showAllNotes() {
        this.isShowAllNotes = !this.isShowAllNotes;
        this.parent.sheets.forEach((sheet) => {
            if (sheet.notes) {
                sheet.notes.forEach((note) => {
                    note.isVisible = this.isShowAllNotes;
                    updateCell(this.parent, sheet, { rowIdx: note.rowIdx, colIdx: note.colIdx, preventEvt: true,
                        cell: { notes: Object.assign({}, note, { isVisible: this.isShowAllNotes }) } });
                });
            }
        });
        const activeSheet = this.parent.getActiveSheet();
        if (activeSheet && activeSheet.notes) {
            activeSheet.notes.forEach((note) => {
                if (this.isShowAllNotes) {
                    this.parent.notify(showNote, { rowIndex: note.rowIdx, columnIndex: note.colIdx, isNoteEditable: false,
                        isScrollWithNote: true });
                }
                else {
                    this.removeNoteElement(note);
                }
            });
        }
    }
    updateNotesFromSheet() {
        let cell;
        this.parent.sheets.forEach((sheet) => {
            if (sheet.notes && sheet.notes.length > 0) {
                sheet.notes.forEach((note) => {
                    if (note.address) {
                        note.rowIdx = note.address[0];
                        note.colIdx = note.address[1];
                        delete note.address;
                        note.id = getUniqueID('e_note');
                        cell = getCell(note.rowIdx, note.colIdx, sheet);
                        if (cell) {
                            cell.notes = note;
                        }
                        else {
                            setCell(note.rowIdx, note.colIdx, sheet, { notes: note });
                        }
                    }
                });
            }
        });
    }
    processSheetNotes(args) {
        const sheet = args.sheet;
        if (!sheet.notes) {
            sheet.notes = [];
        }
        if (args.isDelete) {
            if (args.id) {
                const noteIndex = sheet.notes.findIndex((n) => n.id === args.id);
                if (noteIndex > -1) {
                    const noteToRemove = sheet.notes[noteIndex];
                    this.removeNoteElement(noteToRemove);
                    sheet.notes.splice(noteIndex, 1);
                }
            }
        }
        else if (args.note) {
            const note = args.note;
            if (!note.id) {
                note.id = getUniqueID('e_note');
            }
            if (isNullOrUndefined(note.isVisible)) {
                note.isVisible = false;
            }
            this.syncNoteToSheetArray(sheet, note.rowIdx, note.colIdx, note);
            if (args.isRefresh && !isNullOrUndefined(args.sheetIdx) && args.sheetIdx === this.parent.activeSheetIndex) {
                const targetElement = this.parent.getCell(note.rowIdx, note.colIdx);
                if (targetElement) {
                    if (!targetElement.querySelector('.e-addNoteIndicator')) {
                        this.createNoteIndicator({
                            targetElement: targetElement, rowIndex: note.rowIdx,
                            columnIndex: note.colIdx, skipEvent: false
                        });
                    }
                    if (note.isVisible) {
                        this.showNote({
                            rowIndex: note.rowIdx, columnIndex: note.colIdx, isNoteEditable: false,
                            isScrollWithNote: true, cellElement: targetElement
                        });
                    }
                }
            }
        }
    }
    syncNoteToSheetArray(sheet, rowIdx, colIdx, note) {
        if (!sheet.notes) {
            sheet.notes = [];
        }
        const existingIdx = sheet.notes.findIndex((n) => n.rowIdx === rowIdx && n.colIdx === colIdx);
        if (note) {
            if (existingIdx > -1) {
                sheet.notes[existingIdx] = note;
            }
            else {
                this.insertNoteSorted(sheet, note);
            }
        }
        else {
            if (existingIdx > -1) {
                sheet.notes.splice(existingIdx, 1);
            }
        }
    }
    detachNoteIndicator(rowIdx, colIdx) {
        const cellElement = this.parent.getCell(rowIdx, colIdx);
        if (cellElement) {
            const indicator = cellElement.querySelector('.e-addNoteIndicator');
            if (indicator) {
                EventHandler.remove(indicator, 'mouseover', this.mouseOver);
                EventHandler.remove(indicator, 'mouseout', this.mouseOut);
                delete indicator.dataset.commentListenersAdded;
                delete indicator.dataset.commentRowIndex;
                delete indicator.dataset.commentColIndex;
                detach(indicator);
            }
        }
    }
    noteUndoRedo(args) {
        if (args && args.cellIdx && !isNullOrUndefined(args.sheetIdx)) {
            const sheet = this.parent.sheets[args.sheetIdx];
            const row = args.cellIdx[0];
            const col = args.cellIdx[1];
            if (!sheet.notes) {
                sheet.notes = [];
            }
            const cell = getCell(row, col, sheet);
            const model = cell && cell.notes;
            const existing = sheet.notes.find((n) => n.rowIdx === row && n.colIdx === col);
            if (model) {
                if (existing) {
                    const idxInColl = sheet.notes.findIndex((n) => n.id === existing.id);
                    if (idxInColl > -1) {
                        if (model.id !== existing.id) {
                            model.id = existing.id;
                        }
                        sheet.notes[idxInColl] = model;
                    }
                }
                else {
                    this.insertNoteSorted(sheet, model);
                }
                if (this.parent.activeSheetIndex === args.sheetIdx) {
                    const td = this.parent.getCell(row, col);
                    if (td && !td.querySelector('.e-addNoteIndicator')) {
                        this.createNoteIndicator({ targetElement: td, rowIndex: row, columnIndex: col });
                    }
                    if (model.isVisible) {
                        this.parent.notify(showNote, { rowIndex: row, columnIndex: col, isNoteEditable: false, isScrollWithNote: true });
                    }
                }
            }
            else if (existing) {
                const i = sheet.notes.findIndex((n) => n.id === existing.id);
                if (i > -1) {
                    sheet.notes.splice(i, 1);
                }
                if (this.parent.activeSheetIndex === args.sheetIdx) {
                    this.detachNoteIndicator(row, col);
                    const cellToUpdate = getCell(row, col, sheet);
                    if (cellToUpdate) {
                        delete cellToUpdate.notes;
                        updateCell(this.parent, sheet, { rowIdx: row, colIdx: col, preventEvt: true, cell: cellToUpdate });
                    }
                    const noteContainer = document.getElementById(this.getNoteId(existing));
                    if (noteContainer) {
                        this.removeNoteElement(existing);
                    }
                }
            }
        }
    }
}
